/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { Typography } from "@insite/mobius/index";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import getColor from "@insite/mobius/utilities/getColor";
import { isItCheckoutPage } from "@monsma/App/Utilities/helpers";
import { screen } from "@monsma/XDL/Helpers";
import { zoneTitle } from "@monsma/XDL/Utilities/sharedStyles";
import * as React from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pageType: getCurrentPage(state).type,
});

type Props = PageProps & ReturnType<typeof mapStateToProps>;

export interface FooterStyles {
    page?: MobiusPageProps;
}

export const footerStyles: FooterStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        css: css`
            position: relative;
            background: ${getColor("common.ui6")};
            overflow: visible !important;
            margin-top: 154px;

            ${screen("mixMax", "xs")} {
                margin-top: 261.5px;
            }

            ${screen("mixMax", "sm")} {
                margin-top: 166px;
            }

            ${screen("mixMax", "md")} {
                margin-top: 154px;
            }

            &.checkout-footer {
              margin-top: 80px !important;
            }
        `,
    },
};

const styles = footerStyles;

const Footer = ({ id, pageType }: Props) => {
    const isCmsEditing = getCookie(contentModeCookieName) === "Editing";
    const isFooterPage = pageType === "Footer";
    const showZoneTitle = isCmsEditing && isFooterPage;

    useEffect(() => {
        const handleResize = () => {
            const footer = document.getElementById("siteFooter");
            const stayConnectedWidget = document.getElementById("stayConnectedWidget");

            if (footer && stayConnectedWidget) {
                const height = stayConnectedWidget.offsetHeight / 2;

                stayConnectedWidget.style.top = `-${height}px`;
                footer.style.marginTop = `${height + 80}px`;
            }
        };

        window.addEventListener("resize", handleResize);

        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <Page as="footer" {...styles.page} id="siteFooter"  isFooter={true} className={`${isItCheckoutPage(pageType) && "checkout-footer"}`}>
            {showZoneTitle && <Typography {...zoneTitle}>Stay Connected Zone</Typography>}
            <Zone contentId={id} zoneName="StayConnected" />
            {showZoneTitle && <Typography {...zoneTitle}>Content Zone</Typography>}
            <Zone contentId={id} zoneName="Content" />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, null)(Footer),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
export const FooterContext = "Footer";
